import { claimflowApiClient } from './claimflowApiClient';
import { toWorkflowExecutionError } from './taskService';
import type { WorkflowExecutionData } from './taskService';

/**
 * Claim intervenants (stakeholders) API service — mahhp 22919.
 *
 * Read calls go through origami's generic ERP proxy
 * (`/claimflow/erp/registries/veos/endpoints/{slug}`), which wraps the
 * `ErpProxyResponse<T>` envelope around the raw VEOS payload:
 *
 * - `boby-ws2-gs-sel-idsinp` → `GET /boBy/v2/WS2_GS_SEL_IDSINP` (query `id_sin`
 *   + `perPage`): the claim's intervenants for the « Intervenant » select, in
 *   the standard list envelope `{ total, perPage, page, totalPage, from, to,
 *   data: [{ LIB, VAL }] }` — `VAL` is the `ID_SINP`, `LIB` the label.
 * - `sinper-get` → `GET /sinper/{id_sinp}`: one intervenant as a flat flux
 *   (no envelope — unwrapped defensively like `MiseAJourDossierCard`'s
 *   `unwrapFlux`).
 * - `boby-ws2-gs-tab-intervenants` → `GET /boBy/v2/WS2_GS_TAB_INTERVENANTS`
 *   (query `SES` + `id_sin` + `perPage`, mahhp 22538): the claim's intervenants
 *   for the « Intervenants » table, in the standard list envelope — one
 *   {@link ClaimIntervenantRow} per row.
 *
 * The save is ONE call to the `claimflow-update-stakeholder` backend workflow
 * (`GET /sinper/{id_sinp}` → `PUT /sinper/{id_sinp}` replaying the full flux
 * with only the edited fields replaced, server-side). A failed run (HTTP 422)
 * surfaces as a {@link WorkflowExecutionError} carrying the step trail, exactly
 * like `taskService.closeTask`.
 */

interface ErpProxyResponse<T> {
  success: boolean;
  http_status?: number;
  data: T;
  error?: { code: string; message: string } | null;
}

/** The standard VEOS list envelope (`data` may be omitted on an empty result). */
interface VeosListEnvelope<T> {
  total?: number;
  perPage?: number;
  page?: number;
  totalPage?: number;
  from?: number | null;
  to?: number | null;
  data?: T[] | null;
}

/** One row of `WS2_GS_SEL_IDSINP` — an intervenant of the claim. */
export interface VeosIntervenantOptionRow {
  /** Display label, "NOM | référence" (e.g. `"KWS | 03121213"`). */
  LIB: string;
  /** Claim-intervenant id (`ID_SINP`) — the `{id_sinp}` of `/sinper/{id_sinp}`. */
  VAL: number;
}

/**
 * One row of `WS2_GS_TAB_INTERVENANTS` (mahhp 22538) — an intervenant of the
 * claim, as listed by the « Intervenants » table. The two ids are integers;
 * every other field may be `null` on VEOS' side.
 */
export interface ClaimIntervenantRow {
  /** Person id. */
  ID_PER: number;
  /** Claim-intervenant id — the row's stable key and the `{id_sinp}` of `/sinper/{id_sinp}`. */
  ID_SINP: number;
  /** Category code (the "Catég." column, e.g. `ASSURE`, `TIERS`). */
  CD_CATEG: string | null;
  /** Person number (the "No" column, e.g. `AS374`). */
  NUM_PER: string | null;
  /** Last name / company name (the "Nom" column). */
  NOM_PER: string | null;
  /** First name (the "Prénom" column). */
  PRENOM_PER: string | null;
  /** E-mail (the "Email" column). */
  EMAIL_PER: string | null;
  /** Main phone (the "Tél." column). */
  TELPRINC_PER: string | null;
}

/** An « Intervenant » select option (`value` = `ID_SINP`, `label` = `LIB`). */
export interface IntervenantOption {
  value: number;
  label: string;
}

/** One row of `WS2_GS_SEL_COMPAGNIE` — an insurance company. */
interface VeosCompanyRow {
  CD_CIE: string;
  NOM: string;
}

/** A « Compagnie » select option (`value` = `CD_CIE`, `label` = `NOM`). */
export interface CompanyOption {
  value: string;
  label: string;
}

/**
 * The flat `GET /sinper/{id_sinp}` flux — one intervenant of a claim. Most
 * fields are `null` when not filled in VEOS; the flux carries more keys than
 * listed here (expertise dates…), kept through the index signature.
 */
export interface SinperRecord {
  idPer?: string | number | null;
  idSinp?: number | null;
  idSin?: number | null;
  nom?: string | null;
  prenom?: string | null;
  categ?: string | null;
  telephoneFixe?: string | null;
  telephoneMobile?: string | null;
  email?: string | null;
  adresse1?: string | null;
  adresse2?: string | null;
  adresse3?: string | null;
  ville?: string | null;
  cp?: string | null;
  codePays?: string | null;
  /** `"P"` (personne physique) or `"M"` (personne morale). */
  type?: string | null;
  ref?: string | null;
  cdCie?: string | null;
  numPolTiers?: string | null;
  numSinTiers?: string | null;
  /** Immatriculation. */
  risque1?: string | null;
  risque2?: string | null;
  risque3?: string | null;
  risque4?: string | null;
  risque5?: string | null;
  [key: string]: unknown;
}

/**
 * The editable fields of the « Modifier un intervenant » card, in the order of
 * the `claimflow-update-stakeholder` workflow's `input_params`. Every one is
 * always sent (an empty string when cleared): the workflow writes a missing
 * field as `null`, so omitting one would silently clear it.
 *
 * `telephone` is the flux's `telephoneMobile` both ways: the card preloads it
 * from `telephoneMobile` only and the workflow writes it back there
 * (`telephoneFixe` is replayed unchanged) — never a landline fallback, which
 * would copy the fixe number into `telephoneMobile` on an untouched save.
 */
export const INTERVENANT_UPDATE_FIELDS = [
  'nom',
  'prenom',
  'email',
  'telephone',
  'adresse1',
  'adresse2',
  'cp',
  'ville',
  'type',
  'ref',
  'cdCie',
  'numPolTiers',
  'numSinTiers',
  'risque1',
] as const;

export type IntervenantUpdateField = (typeof INTERVENANT_UPDATE_FIELDS)[number];

/** Values of the editable fields — `null` / `undefined` are sent as `''`. */
export type IntervenantUpdateValues = Partial<
  Record<IntervenantUpdateField, string | null | undefined>
>;

const UPDATE_STAKEHOLDER_WORKFLOW_URL =
  '/claimflow/workflows/claimflow-update-stakeholder/execute';

const SEL_IDSINP_URL =
  '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-idsinp';

const SINPER_GET_URL = '/claimflow/erp/registries/veos/endpoints/sinper-get';

const SEL_COMPAGNIE_URL =
  '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-sel-compagnie';

const TAB_INTERVENANTS_URL =
  '/claimflow/erp/registries/veos/endpoints/boby-ws2-gs-tab-intervenants';

function unwrap<T>(envelope: ErpProxyResponse<T>): T {
  if (!envelope.success) {
    throw new Error(envelope.error?.message ?? 'ERP request failed');
  }
  return envelope.data;
}

/**
 * The `sinper-get` flux. The endpoint is non-paginated, so the flux lives at
 * the envelope's `data` — but defensively handle the `{ total, data: [flux] }`
 * list envelope too (same rule as `MiseAJourDossierCard`'s `unwrapFlux`).
 */
function unwrapSinper(data: unknown): SinperRecord | null {
  if (typeof data !== 'object' || data === null) return null;
  const maybeEnvelope = data as { data?: unknown };
  if (Array.isArray(maybeEnvelope.data)) {
    const [first] = maybeEnvelope.data as unknown[];
    return typeof first === 'object' && first !== null ? (first as SinperRecord) : null;
  }
  return data as SinperRecord;
}

export const intervenantService = {
  /**
   * The claim's intervenants for the « Intervenant » select
   * (`WS2_GS_SEL_IDSINP`), in VEOS order. `perPage: 9999` overrides VEOS's
   * default 15-row page — the codebase convention for full lists.
   */
  async fetchIntervenantOptions(idSin: string | number): Promise<IntervenantOption[]> {
    const response = await claimflowApiClient.get<
      ErpProxyResponse<VeosListEnvelope<VeosIntervenantOptionRow> | null>
    >(SEL_IDSINP_URL, { params: { id_sin: idSin, perPage: 9999 } });

    const rows = unwrap(response.data)?.data ?? [];
    return rows.map((row) => ({ value: row.VAL, label: row.LIB }));
  },

  /**
   * The claim's intervenants for the « Intervenants » table
   * (`WS2_GS_TAB_INTERVENANTS`, mahhp 22538), in VEOS order. The Veos session
   * travels as `SES` (like `taskService.fetchCosts` on `WS2_GS_TAB_SINGAR`);
   * `perPage: 9999` overrides VEOS's default 15-row page — the codebase
   * convention for full lists.
   *
   * @throws when the proxy reports a failure.
   */
  async fetchClaimIntervenants(
    ses: string | number,
    idSin: string | number,
  ): Promise<ClaimIntervenantRow[]> {
    const response = await claimflowApiClient.get<
      ErpProxyResponse<VeosListEnvelope<ClaimIntervenantRow> | null>
    >(TAB_INTERVENANTS_URL, { params: { SES: ses, id_sin: idSin, perPage: 9999 } });

    return unwrap(response.data)?.data ?? [];
  },

  /**
   * The « Compagnie » choices (`WS2_GS_SEL_COMPAGNIE`) — the same call as the
   * create form (`IntervenantForm`): fetch-all, `CD_CIE` / `NOM`.
   */
  async fetchCompanyOptions(): Promise<CompanyOption[]> {
    const response = await claimflowApiClient.get<
      ErpProxyResponse<VeosListEnvelope<VeosCompanyRow> | null>
    >(SEL_COMPAGNIE_URL, { params: { perPage: 9999 } });

    const rows = unwrap(response.data)?.data ?? [];
    return rows.map((row) => ({ value: row.CD_CIE, label: row.NOM }));
  },

  /**
   * One intervenant of the claim (`GET /sinper/{id_sinp}`) — the path param
   * travels through `params`, like `sinistre-inline-get`'s `id_sin`.
   *
   * @throws when the proxy reports a failure or returns no flux.
   */
  async fetchIntervenant(idSinp: number): Promise<SinperRecord> {
    const response = await claimflowApiClient.get<ErpProxyResponse<unknown>>(
      SINPER_GET_URL,
      { params: { id_sinp: idSinp } },
    );

    const record = unwrapSinper(unwrap(response.data));
    if (!record) {
      throw new Error('Intervenant introuvable.');
    }
    return record;
  },

  /**
   * Save the edited intervenant through the `claimflow-update-stakeholder`
   * workflow — one call. `context_data` carries `id_sinp` plus EVERY editable
   * field as a string (`''` when cleared), so a cleared field is saved
   * cleared.
   *
   * @throws {WorkflowExecutionError} when the workflow run fails (HTTP 422) —
   *   carries the backend message and the step trail.
   */
  async updateIntervenant(
    idSinp: number,
    fields: IntervenantUpdateValues,
  ): Promise<WorkflowExecutionData> {
    const contextData: Record<string, string | number> = { id_sinp: idSinp };
    for (const key of INTERVENANT_UPDATE_FIELDS) {
      contextData[key] = fields[key] ?? '';
    }

    try {
      const response = await claimflowApiClient.post<{ data: WorkflowExecutionData }>(
        UPDATE_STAKEHOLDER_WORKFLOW_URL,
        { context_data: contextData },
      );
      return response.data.data;
    } catch (error) {
      const workflowError = toWorkflowExecutionError(error);
      if (workflowError) throw workflowError;
      throw error;
    }
  },
};
